import { useQuery } from '@apollo/client/react';
import GET_BRANDS_FOR_ACCOUNT_Q from '../gql/getBrandsForAccount.gql';

import { Table } from '@repo/ui/Table';
import { PAGE_SCHEMA, Pagination, decodePaging } from '@repo/ui/Pagination';
import { z } from 'zod';
import { useZodQuery } from '@repo/hooks/useZodQuery';
import { coerce } from '@repo/util/coerceForTable';
import { toQueryList } from '@repo/util/toQueryList';

import { coerceValue } from '@repo/util/coerceValueForQuery';
import { Form } from '@repo/ui/Form';
import BrandsTableFilter from './BrandsTableFilter';
import { POLICIES } from '@/lib/Authorization';
import { useUser } from '@repo/hooks/useUser';
import { ButtonLink } from '@repo/ui/Button';
import { Link } from 'react-router';

const BRANDS_QUERY_SCHEMA = PAGE_SCHEMA.extend({
  display_name: z.string().default(''),
  tcr_brand_id: z
    .string()
    .max(7, { message: 'A campaign ID cannot be more than 7 characters' })
    .default(''),
  identity_status: z
    .enum(['VERIFIED', 'VETTED_VERIFIED', 'UNVERIFIED', 'SELF_DECLARED', ''])
    .optional()
    .default(''),
});

const PAGE_SIZE = 10;

export interface BrandsTableProps {
  accountId?: string;
}

const BrandsTable = ({ accountId }: BrandsTableProps) => {
  const user = useUser();

  const canViewBrand = POLICIES['csp_brands:show'](user);

  const filter = useZodQuery({
    schema: BRANDS_QUERY_SCHEMA,
    ignoreList: ['page'],
    coerce,
  });

  const { page, ...queryFields } = filter.queryValues;
  const queryList = [
    ...toQueryList(
      {
        ...queryFields,
      },
      coerceValue,
      ['display_name', 'tcr_brand_id']
    ),
  ];

  const { loading, error, data } = useQuery(GET_BRANDS_FOR_ACCOUNT_Q, {
    variables: {
      ...decodePaging(page, PAGE_SIZE),
      queryList,
      id: accountId,
    },
    notifyOnNetworkStatusChange: true,
  });

  const { edges: cspBrandNodes, pageInfo } = data?.cspBrandsForAccount ?? {};

  return (
    <div>
      <Form {...filter} variant="horizontal" className={'mb-6'}>
        <BrandsTableFilter />
      </Form>
      <Table
        loading={loading}
        pageSize={PAGE_SIZE}
        data={cspBrandNodes?.map((cspBrand) => cspBrand.node) ?? []}
        dataType="brands"
        columns={[
          {
            title: 'Name',
            Cell({ entry: { displayName, id } }) {
              return POLICIES['csp_brands:show'](user) ? (
                <Link to={`/a/${accountId}/tcr/brands/${id}`} className="underline">
                  {displayName}
                </Link>
              ) : (
                <span> {displayName}</span>
              );
            },
          },
          {
            title: 'Status',
            width: 'md',
            Cell({ entry: { identityStatus } }) {
              return (
                <div className="">
                  <div className="relative flex items-center gap-2">
                    <span className="">{identityStatus}</span>
                  </div>
                </div>
              );
            },
          },

          {
            title: 'Brand ID',
            Cell({ entry: { id, tcr_brand_id } }) {
              return POLICIES['csp_brands:show'](user) ? (
                <Link to={`/a/${accountId}/tcr/brands/${id}`} className="underline">
                  {tcr_brand_id}
                </Link>
              ) : (
                <span> {tcr_brand_id}</span>
              );
            },
          },

          {
            title: 'Account',
            Cell({ entry: { account } }) {
              return POLICIES['accounts:show'](user) ? (
                <Link className="underline" to={`/a/${account?.id}`}>
                  {account?.name}
                </Link>
              ) : (
                <span> {account?.name}</span>
              );
            },
          },
          {
            title: canViewBrand ? 'Details' : '',
            width: canViewBrand ? 'sm' : 'hidden',
            Cell({ entry: { id } }) {
              return canViewBrand ? (
                <ButtonLink as={Link} variant="secondary" href={`/a/${accountId}/tcr/brands/${id}`}>
                  Details
                </ButtonLink>
              ) : (
                <></>
              );
            },
          },
        ]}
        error={!!error}
      />
      {!error ? <Pagination {...{ pageInfo, filter }} /> : null}
    </div>
  );
};

export default BrandsTable;
